import { GEM_KINDS, type GemKind } from './types';
import type { Rng } from './intent';

/**
 * Weights for NATURALLY generated gems — seeding the well, Metronome, anything
 * that invents a colour rather than being told one.
 *
 * Authored content ignores this entirely. An enemy piece carries explicit gem
 * kinds, a card's printed colours never change, and a relic's starting piece
 * is what it says it is. Cutting a colour thins what the well produces on its
 * own; it does not erase the colour from the game. That distinction is the
 * whole mechanic — a "removed" colour keeps arriving through opponents, and
 * matches in it become something the player manufactures deliberately.
 *
 * Stone is weighted 0: it is never randomly generated. It enters only through
 * enemies, and that is what makes an enemy's Stone meaningful.
 */
export type GemWeights = Record<GemKind, number>;

export const DEFAULT_GEM_WEIGHTS: GemWeights = {
  ember: 1,
  frost: 1,
  venom: 1,
  volt: 1,
  stone: 0,
};

/**
 * Fraction a cut leaves behind. Reduction, not removal — at 0.4 a four-colour
 * pool drops the cut colour from 25% to about 12%, which thins it without
 * making the board deterministic. A second cut compounds.
 */
export const CUT_FACTOR = 0.4;

export function cloneWeights(w: GemWeights): GemWeights {
  return { ...w };
}

/** Sample one colour by weight, or null if nothing is generatable. */
export function pickWeightedGem(rng: Rng, w: GemWeights): GemKind | null {
  let total = 0;
  for (const k of GEM_KINDS) total += w[k] ?? 0;
  if (total <= 0) return null;
  let roll = rng() * total;
  for (const k of GEM_KINDS) {
    roll -= w[k] ?? 0;
    if (roll <= 0) return k;
  }
  return null;
}

/** Reduce one colour's weight. Never removes it — see CUT_FACTOR. */
export function cutGem(w: GemWeights, kind: GemKind, factor = CUT_FACTOR): GemWeights {
  const next = cloneWeights(w);
  const current = next[kind] ?? 0;
  next[kind] = current > 0 ? current * factor : 0;
  return next;
}

/** Each generatable colour's share of the pool, for display. */
export function composition(w: GemWeights): { kind: GemKind; share: number; weight: number }[] {
  let total = 0;
  for (const k of GEM_KINDS) total += w[k] ?? 0;
  return GEM_KINDS.map(k => {
    const weight = w[k] ?? 0;
    return { kind: k, share: total > 0 ? weight / total : 0, weight };
  }).filter(e => e.weight > 0);
}

/** The colour this weights object generates most often, or null. */
export function heaviestGem(w: GemWeights): GemKind | null {
  let best: GemKind | null = null;
  let bestW = 0;
  for (const k of GEM_KINDS) {
    const weight = w[k] ?? 0;
    if (weight > bestW) {
      bestW = weight;
      best = k;
    }
  }
  return best;
}
